/**
 * Public Stylist Profile — /stylists/:stylistId
 *
 * Renders the full stylist storefront: cover + avatar + headline, "Why this
 * match?" popover using the reasons array from the AI Style Match engine,
 * bio, specialties, languages, city, experience, links, and Phase-3
 * placeholder CTAs for Book Consultation / Ask Question (both wired to
 * open a toast until Batch 3 ships the booking flow).
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/lib/AuthProvider";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Loader2, BadgeCheck, MapPin, Instagram, ExternalLink, Star, Sparkles,
  ArrowLeft, MessageSquare, Calendar, Info, ShoppingBag,
} from "lucide-react";
import { motion } from "framer-motion";
import { prettySpecialty, StyleMatchPill, VerificationBadge } from "@/pages/stylists/_shared";
import { BookingModal, PrebookChatModal } from "@/pages/stylists/BookingModals";
import { SERVICE_KIND_META } from "@/pages/stylists/StudioTabs";

export default function StylistProfile() {
  const { stylistId } = useParams();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [whyOpen, setWhyOpen] = useState(false);
  const [looks, setLooks] = useState([]);
  const [services, setServices] = useState([]);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [prebookOpen, setPrebookOpen] = useState(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setNotFound(false);
    api.get(`/stylists/${stylistId}`)
      .then(({ data }) => {
        if (cancel) return;
        setProfile(data.profile);
      })
      .catch((e) => {
        if (cancel) return;
        if (e?.response?.status === 404) setNotFound(true);
      })
      .finally(() => { if (!cancel) setLoading(false); });
    // Public looks + services can load in parallel; both 404 if unapproved.
    api.get(`/stylists/${stylistId}/looks`).then(({ data }) => !cancel && setLooks(data.items || [])).catch(() => {});
    api.get(`/stylists/${stylistId}/services`).then(({ data }) => !cancel && setServices(data.items || [])).catch(() => {});
    return () => { cancel = true; };
  }, [stylistId]);

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  }
  if (notFound || !profile) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <p className="eyebrow">404</p>
        <h1 className="editorial-h2 mt-4">Stylist not found.</h1>
        <Link to="/stylists" className="inline-flex items-center gap-2 mt-6 text-sm underline underline-offset-4">
          <ArrowLeft size={14} /> Back to marketplace
        </Link>
      </div>
    );
  }

  const cover = profile.cover_url ? fileUrl(profile.cover_url) : null;
  const isOwner = user?.user_id === profile.user_id;
  const match = profile.style_match?.score;
  const reasons = profile.style_match?.reasons || [];

  return (
    <div className="max-w-5xl mx-auto pb-24" data-testid="stylist-profile-page">
      {/* Cover */}
      <div className="relative aspect-[16/9] md:aspect-[21/9] bg-muted overflow-hidden">
        {cover ? (
          <img src={cover} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-muted to-background flex items-center justify-center">
            <span className="font-serif text-9xl text-muted-foreground/30 tracking-tight">
              {(profile.display_name || "?").charAt(0)}
            </span>
          </div>
        )}
        <Link
          to="/stylists"
          className="absolute top-4 left-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] bg-background/80 backdrop-blur px-3 py-2 border border-border"
        >
          <ArrowLeft size={12} /> Back
        </Link>
      </div>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="px-6 lg:px-10 -mt-6 relative z-10"
      >
        <div className="bg-background border border-border p-6 md:p-8">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-serif text-4xl md:text-5xl leading-tight truncate">
                  {profile.display_name}
                </h1>
                {profile.verification_status === "approved" && (
                  <BadgeCheck className="text-accent shrink-0" size={22} />
                )}
                {profile.featured && (
                  <span className="text-[10px] uppercase tracking-[0.22em] px-2 py-0.5 bg-accent text-accent-foreground">
                    Featured
                  </span>
                )}
                {isOwner && <VerificationBadge status={profile.verification_status} />}
              </div>
              {profile.headline && (
                <p className="text-sm text-muted-foreground mt-2">{profile.headline}</p>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                {profile.city && (
                  <span className="inline-flex items-center gap-1"><MapPin size={12} /> {profile.city}</span>
                )}
                {profile.experience_years > 0 && (
                  <span className="uppercase tracking-[0.16em]">{profile.experience_years} years</span>
                )}
                {profile.rating > 0 && (
                  <span className="inline-flex items-center gap-1">
                    <Star size={12} className="fill-current" /> {profile.rating.toFixed(1)} ({profile.reviews_count})
                  </span>
                )}
                <span className="uppercase tracking-[0.16em]">{profile.price_tier} tier</span>
              </div>
            </div>

            <div className="flex flex-col items-end gap-2 shrink-0">
              {typeof match === "number" && !isOwner && (
                <div className="text-right">
                  <StyleMatchPill score={match} />
                  {reasons.length > 0 && (
                    <button
                      data-testid="why-match-btn"
                      onClick={() => setWhyOpen((v) => !v)}
                      className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground mt-2 inline-flex items-center gap-1"
                    >
                      <Info size={11} /> Why this match?
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {whyOpen && reasons.length > 0 && (
            <div
              data-testid="why-match-popover"
              className="mt-4 border-l-2 border-accent bg-muted/40 p-4"
            >
              <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2 flex items-center gap-1.5">
                <Sparkles size={11} /> Why we matched {match}%
              </p>
              <ul className="space-y-1.5 text-sm">
                {reasons.map((r, i) => <li key={i} className="leading-relaxed">· {r}</li>)}
              </ul>
            </div>
          )}

          {/* CTAs */}
          {!isOwner && profile.verification_status === "approved" && (
            <div className="mt-6 flex gap-3 flex-wrap">
              <Button
                data-testid="stylist-book-btn"
                onClick={() => setBookingOpen(true)}
                disabled={services.length === 0}
                className="rounded-none h-11 uppercase tracking-[0.2em] text-[10px] px-5 gap-2"
              >
                <Calendar size={14} /> Book consultation
              </Button>
              <Button
                data-testid="stylist-ask-btn"
                variant="outline"
                onClick={() => setPrebookOpen(true)}
                className="rounded-none h-11 uppercase tracking-[0.2em] text-[10px] px-5 gap-2"
              >
                <MessageSquare size={14} /> Ask a question
              </Button>
            </div>
          )}
          {!isOwner && services.length === 0 && profile.verification_status === "approved" && (
            <p className="text-[11px] text-muted-foreground mt-2">
              This stylist hasn't published services yet — use "Ask a question" to reach out.
            </p>
          )}
          {isOwner && (
            <div className="mt-6">
              <Link to="/stylist-studio">
                <Button
                  data-testid="stylist-manage-btn"
                  variant="outline"
                  className="rounded-none h-11 uppercase tracking-[0.2em] text-[10px] px-5"
                >
                  Manage in Studio
                </Button>
              </Link>
            </div>
          )}
        </div>
      </motion.div>

      {/* Content grid */}
      <div className="px-6 lg:px-10 mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-6">
          <Section title="About">
            <p className="text-sm leading-relaxed whitespace-pre-line">{profile.bio}</p>
          </Section>
          <Section title="Specialties">
            <div className="flex flex-wrap gap-2">
              {(profile.specialties || []).map((s) => (
                <span key={s} className="text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 border border-border">
                  {prettySpecialty(s)}
                </span>
              ))}
            </div>
          </Section>
          <Section title="Portfolio">
            {looks.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No looks published yet. Follow the stylist's Instagram for their latest work.
              </p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="stylist-looks-grid">
                {looks.map((l) => (
                  <a
                    key={l.id}
                    href={l.buy_url || "#"}
                    onClick={(e) => { if (!l.buy_url) e.preventDefault(); }}
                    target={l.buy_url ? "_blank" : undefined}
                    rel={l.buy_url ? "noreferrer" : undefined}
                    className="border border-border hover:border-foreground transition-colors group block"
                    data-testid={`stylist-look-${l.id}`}
                  >
                    <div className="aspect-[4/5] bg-muted overflow-hidden">
                      <img src={fileUrl(l.image_path)} alt={l.title} className="w-full h-full object-cover" />
                    </div>
                    <div className="p-2.5">
                      <p className="text-xs font-serif line-clamp-1">{l.title}</p>
                      <div className="flex items-center justify-between mt-1 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                        {l.price_inr > 0 ? <span>₹{l.price_inr.toLocaleString("en-IN")}</span> : <span>—</span>}
                        {l.buy_url && <ShoppingBag size={11} className="group-hover:text-foreground" />}
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            )}
          </Section>
          {services.length > 0 && (
            <Section title="Services">
              <div className="space-y-2" data-testid="stylist-services-list">
                {services.map((s) => {
                  const M = SERVICE_KIND_META[s.kind] || {};
                  const Icon = M.icon;
                  return (
                    <div key={s.id} className="flex items-center gap-3 p-3 border border-border" data-testid={`stylist-service-${s.id}`}>
                      {Icon && <Icon size={16} className="shrink-0 text-muted-foreground" />}
                      <div className="flex-1 min-w-0">
                        <p className="font-serif text-sm">{s.title}</p>
                        <p className="text-xs text-muted-foreground">{s.duration_min} min</p>
                      </div>
                      <p className="text-sm font-serif">₹{s.price_inr.toLocaleString("en-IN")}</p>
                    </div>
                  );
                })}
              </div>
            </Section>
          )}
        </div>

        <aside className="space-y-6">
          <Section title="Details">
            <dl className="space-y-3 text-sm">
              {profile.experience_years > 0 && <Row label="Experience" value={`${profile.experience_years} years`} />}
              {profile.languages?.length > 0 && <Row label="Languages" value={profile.languages.join(" · ")} />}
              {profile.hourly_rate_inr > 0 && <Row label="Starting from" value={`₹${profile.hourly_rate_inr.toLocaleString("en-IN")}`} />}
              <Row label="Price tier" value={profile.price_tier} />
              <Row label="Serves" value={profile.target_gender === "all" ? "Everyone" : profile.target_gender === "male" ? "Men" : "Women"} />
            </dl>
          </Section>
          {(profile.instagram || profile.portfolio_url) && (
            <Section title="Links">
              <div className="space-y-2 text-sm">
                {profile.instagram && (
                  <a
                    data-testid="stylist-instagram-link"
                    href={`https://instagram.com/${profile.instagram.replace(/^@/, "")}`}
                    target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-2 underline underline-offset-4"
                  >
                    <Instagram size={14} /> @{profile.instagram.replace(/^@/, "")}
                  </a>
                )}
                {profile.portfolio_url && (
                  <a
                    data-testid="stylist-portfolio-link"
                    href={profile.portfolio_url.startsWith("http") ? profile.portfolio_url : `https://${profile.portfolio_url}`}
                    target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-2 underline underline-offset-4"
                  >
                    <ExternalLink size={14} /> Portfolio
                  </a>
                )}
              </div>
            </Section>
          )}
        </aside>
      </div>

      <BookingModal open={bookingOpen} onClose={() => setBookingOpen(false)} stylist={profile} />
      <PrebookChatModal
        open={prebookOpen}
        onClose={() => setPrebookOpen(false)}
        stylist={profile}
        onBookNow={() => { setPrebookOpen(false); setBookingOpen(true); }}
      />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="border border-border p-5">
      <p className="eyebrow mb-4">{title}</p>
      {children}
    </section>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-border pb-2 last:border-b-0 last:pb-0">
      <dt className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{label}</dt>
      <dd className="text-sm font-serif capitalize">{value}</dd>
    </div>
  );
}
